/* eslint-disable */
import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Iconify from '../components/iconify';
import InfoTooltip from '../components/InfoTooltip';
import { getDatabaseCollectionDocuments, getDatabaseCollections } from '../helper/backend_helper';

const PAGE_SIZE = 25;

const formatDocument = (document) => JSON.stringify(document, null, 2);

export default function DatabaseExplorer() {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [filterText, setFilterText] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [error, setError] = useState('');

  const selectedCount = useMemo(
    () => collections.find((collection) => collection.name === selectedCollection)?.count ?? 0,
    [collections, selectedCollection]
  );

  const fetchCollections = async () => {
    setLoadingCollections(true);
    setError('');
    try {
      const response = await getDatabaseCollections();
      const rows = response?.data?.data || [];
      setCollections(rows);
      if (!selectedCollection && rows.length) setSelectedCollection(rows[0].name);
    } catch (err) {
      setError(err?.response?.data?.errors?.[0]?.detail || 'Could not load database collections.');
    } finally {
      setLoadingCollections(false);
    }
  };

  const fetchDocuments = async (collectionName = selectedCollection, nextSkip = skip, nextFilter = appliedFilter) => {
    if (!collectionName) return;
    setLoadingDocuments(true);
    setError('');
    try {
      const response = await getDatabaseCollectionDocuments(collectionName, {
        skip: nextSkip,
        limit: PAGE_SIZE,
        filter: nextFilter || undefined,
      });
      const payload = response?.data?.data || {};
      setDocuments(payload.documents || []);
      setTotal(payload.total || 0);
      setSkip(payload.skip || 0);
    } catch (err) {
      setError(err?.response?.data?.errors?.[0]?.detail || 'Could not load collection documents. Check the JSON filter.');
    } finally {
      setLoadingDocuments(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  useEffect(() => {
    if (selectedCollection) fetchDocuments(selectedCollection, 0, appliedFilter);
  }, [selectedCollection]);

  const applyFilter = () => {
    if (filterText.trim()) {
      try {
        JSON.parse(filterText);
      } catch (err) {
        setError('Filter must be valid JSON, for example {"guestName":"Mario"}.');
        return;
      }
    }
    setAppliedFilter(filterText.trim());
    fetchDocuments(selectedCollection, 0, filterText.trim());
  };

  const clearFilter = () => {
    setFilterText('');
    setAppliedFilter('');
    fetchDocuments(selectedCollection, 0, '');
  };

  const goToPage = (direction) => {
    const nextSkip = Math.max(0, skip + direction * PAGE_SIZE);
    fetchDocuments(selectedCollection, nextSkip, appliedFilter);
  };

  return (
    <>
      <Helmet>
        <title>Database Explorer</title>
      </Helmet>

      <Container maxWidth={false} sx={{ py: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h4">
              Database Explorer{' '}
              <InfoTooltip
                width={20}
                title="A read-only window into the raw MongoDB collections behind the platform, mainly for support and debugging. Pick a collection on the left to browse its documents as formatted JSON, paged 25 at a time. Use the JSON filter field to narrow results (for example {name: Mario}) and Refresh to reload the latest data; nothing here can be edited."
              />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Read-only view of the self-hosted MongoDB collections.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="mdi:refresh" />}
            onClick={() => {
              fetchCollections();
              fetchDocuments(selectedCollection, skip, appliedFilter);
            }}
          >
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', lg: 'row' }} gap={2} alignItems="stretch">
          <Card sx={{ width: { xs: '100%', lg: 340 }, p: 2, flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6">Collections</Typography>
              {loadingCollections && <CircularProgress size={18} />}
            </Stack>
            <List dense sx={{ maxHeight: '72vh', overflow: 'auto' }}>
              {collections.map((collection) => (
                <ListItemButton
                  key={collection.name}
                  selected={collection.name === selectedCollection}
                  onClick={() => {
                    setSelectedCollection(collection.name);
                    setSkip(0);
                  }}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    primary={collection.name}
                    secondary={`${collection.count.toLocaleString()} documents`}
                    primaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Card>

          <Card sx={{ p: 2, flex: 1, minWidth: 0 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" gap={2}>
              <Box>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="h6">{selectedCollection || 'Select a collection'}</Typography>
                  {selectedCollection && <Chip size="small" label={`${selectedCount.toLocaleString()} estimated`} />}
                  <Chip size="small" color="success" variant="outlined" label="Read only" />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Showing {documents.length ? skip + 1 : 0}-{Math.min(skip + documents.length, total)} of {total.toLocaleString()}
                </Typography>
              </Box>

              <Stack direction="row" alignItems="center" gap={1}>
                <IconButton disabled={skip === 0 || loadingDocuments} onClick={() => goToPage(-1)}>
                  <Iconify icon="mdi:chevron-left" />
                </IconButton>
                <IconButton disabled={skip + PAGE_SIZE >= total || loadingDocuments} onClick={() => goToPage(1)}>
                  <Iconify icon="mdi:chevron-right" />
                </IconButton>
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} mb={2}>
              <TextField
                fullWidth
                size="small"
                label='JSON filter, e.g. {"name":"Mario"}'
                value={filterText}
                onChange={(event) => setFilterText(event.target.value)}
              />
              <Button variant="contained" onClick={applyFilter} disabled={!selectedCollection || loadingDocuments}>
                Apply
              </Button>
              <Button variant="outlined" onClick={clearFilter} disabled={!selectedCollection || loadingDocuments}>
                Clear
              </Button>
            </Stack>

            {loadingDocuments ? (
              <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 280 }}>
                <CircularProgress />
              </Stack>
            ) : (
              <Stack gap={1.5} sx={{ maxHeight: '67vh', overflow: 'auto' }}>
                {documents.map((document) => (
                  <Box
                    key={document._id || JSON.stringify(document).slice(0, 60)}
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: '#0f172a',
                      color: '#e5e7eb',
                      borderRadius: 1,
                      fontSize: 12,
                      lineHeight: 1.55,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {formatDocument(document)}
                  </Box>
                ))}
                {!documents.length && <Typography color="text.secondary">No documents found.</Typography>}
              </Stack>
            )}
          </Card>
        </Stack>
      </Container>
    </>
  );
}
